// src/components/users/UserRolesModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assignUserRoles } from '../../store/actions/userActions';
import { fetchRoles } from '../../store/actions/roleActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const UserRolesModal = ({ isOpen, onClose, user }) => {
  const dispatch = useDispatch();
  const { roles, loading } = useSelector((state) => state.roles);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Compute filtered roles using useMemo (defined before any useEffect that uses it)
  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [roles, searchTerm]);

  // Compute selectAll state
  const selectAll = useMemo(() => {
    return filteredRoles.length > 0 && filteredRoles.every(role => selectedRoles.includes(role.id));
  }, [filteredRoles, selectedRoles]);

  // Load roles when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchRoles({ limit: 100, includePermissions: false }));
    }
  }, [dispatch, isOpen]);

  // Initialize selected roles from user's current roles
  useEffect(() => {
    if (user?.roles && isOpen) {
      const userRoleIds = user.roles.map(r => r.id);
      setSelectedRoles(userRoleIds);
    } else {
      setSelectedRoles([]);
    }
  }, [user, isOpen]);

  const handleRoleToggle = useCallback((roleId) => {
    setSelectedRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      // Deselect all filtered roles
      const filteredIds = filteredRoles.map(r => r.id);
      setSelectedRoles(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered roles
      const filteredIds = filteredRoles.map(r => r.id);
      setSelectedRoles(prev => [...new Set([...prev, ...filteredIds])]);
    }
  }, [selectAll, filteredRoles]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await dispatch(assignUserRoles(user.id, selectedRoles));
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to assign roles');
    } finally {
      setSaving(false);
    }
  }, [dispatch, user, selectedRoles, onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Styles object
  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      overflowY: 'auto',
      animation: 'fadeIn 0.2s ease-out',
    },
    overlayBackground: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      transition: 'opacity 0.2s',
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '1rem',
      textAlign: 'center',
    },
    modal: {
      display: 'inline-block',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      transform: 'translateY(0)',
      transition: 'all 0.2s',
      maxWidth: '42rem',
      width: '100%',
      animation: 'slideUp 0.3s ease-out',
    },
    content: {
      padding: '1.5rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: '1.5rem',
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '0.25rem',
      lineHeight: 1,
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    closeButtonHover: {
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
    },
    
    // User info
    userInfo: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      padding: '1rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    userAvatar: {
      width: '3rem',
      height: '3rem',
      borderRadius: '9999px',
      backgroundColor: '#dbeafe',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    userAvatarText: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1e40af',
    },
    userDetails: {
      flex: 1,
    },
    userName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    userEmail: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    
    // Search
    searchContainer: {
      marginBottom: '1rem',
    },
    searchInput: {
      width: '100%',
      padding: '0.625rem 1rem 0.625rem 2.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '0.75rem center',
      backgroundSize: '1.25rem',
    },
    searchInputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    
    // Select All
    selectAllContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.5rem 0',
      marginBottom: '0.5rem',
      borderBottom: '1px solid #e5e7eb',
    },
    selectAllLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      cursor: 'pointer',
    },
    selectAllCheckbox: {
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    selectAllCount: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    
    // Roles list
    rolesList: {
      maxHeight: '24rem',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      marginBottom: '1rem',
    },
    roleItem: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    roleItemHover: {
      backgroundColor: '#f9fafb',
    },
    roleItemSelected: {
      backgroundColor: '#eff6ff',
    },
    roleItemLast: {
      borderBottom: 'none',
    },
    roleCheckbox: {
      marginTop: '0.125rem',
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    roleContent: {
      marginLeft: '0.75rem',
      flex: 1,
    },
    roleName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    roleDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    roleMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '0.25rem',
    },
    roleBadge: {
      padding: '0.125rem 0.375rem',
      fontSize: '0.625rem',
      fontWeight: 500,
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      borderRadius: '0.25rem',
    },
    
    // Empty state
    emptyState: {
      padding: '2rem',
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    emptyStateIcon: {
      width: '3rem',
      height: '3rem',
      margin: '0 auto 1rem',
      color: '#d1d5db',
    },
    
    // Loading state
    loadingContainer: {
      padding: '3rem',
      textAlign: 'center',
    },
    
    // Actions
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      marginTop: '1.5rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    saveButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    saveButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    
    // Stats
    statsContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
      fontSize: '0.875rem',
    },
    statsSelected: {
      color: '#2563eb',
      fontWeight: 500,
    },
    statsTotal: {
      color: '#6b7280',
    },
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.overlayBackground} />
      
      <div style={styles.container}>
        <div style={styles.modal}>
          <div style={styles.content}>
            {/* Header */}
            <div style={styles.header}>
              <h3 style={styles.title}>
                Assigner des rôles à {user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'l\'utilisateur'}
              </h3>
              <button
                onClick={onClose}
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = styles.closeButtonHover.color;
                  e.currentTarget.style.backgroundColor = styles.closeButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = styles.closeButton.color;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>

            {/* User Info */}
            {user && (
              <div style={styles.userInfo}>
                <div style={styles.userAvatar}>
                  <span style={styles.userAvatarText}>
                    {user.firstName?.[0] || ''}
                    {user.lastName?.[0] || ''}
                  </span>
                </div>
                <div style={styles.userDetails}>
                  <div style={styles.userName}>
                    {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim()}
                  </div>
                  <div style={styles.userEmail}>{user.email}</div>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <Alert
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            )}

            {/* Search */}
            <div style={styles.searchContainer}>
              <input
                type="text"
                placeholder="Chercher des rôles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = styles.searchInputFocus.borderColor;
                  e.currentTarget.style.boxShadow = styles.searchInputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Stats and Select All */}
            <div style={styles.statsContainer}>
              <span style={styles.statsSelected}>
                {selectedRoles.length} sélectionnés
              </span>
              <span style={styles.statsTotal}>
                sur {filteredRoles.length} rôles
              </span>
            </div>

            <div style={styles.selectAllContainer}>
              <label style={styles.selectAllLabel}>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  style={styles.selectAllCheckbox}
                />
                Sélectionner tout
              </label>
              <span style={styles.selectAllCount}>
                {filteredRoles.length} rôles
              </span>
            </div>

            {/* Roles List */}
            {loading ? (
              <div style={styles.loadingContainer}>
                <LoadingSpinner />
              </div>
            ) : (
              <div style={styles.rolesList}>
                {filteredRoles.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const isSelected = selectedRoles.includes(role.id);
                    const isLast = index === filteredRoles.length - 1;
                    
                    return (
                      <div
                        key={role.id}
                        style={{
                          ...styles.roleItem,
                          ...(isSelected ? styles.roleItemSelected : {}),
                          ...(isLast ? styles.roleItemLast : {}),
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = styles.roleItemHover.backgroundColor;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={() => handleRoleToggle(role.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={styles.roleCheckbox}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div style={styles.roleContent}>
                          <div style={styles.roleName}>{role.name}</div>
                          {role.description && (
                            <div style={styles.roleDescription}>{role.description}</div>
                          )}
                          <div style={styles.roleMeta}>
                            <span style={styles.roleBadge}>
                              {role._count?.userRoles || 0} utilisateurs
                            </span>
                            {role.rolePermissions && (
                              <span style={styles.roleBadge}>
                                {role.rolePermissions.length} permissions
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={styles.emptyState}>
                    <svg style={styles.emptyStateIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p>Aucun rôle trouvé correspondant à "{searchTerm}"</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={styles.actions}>
              <button
                type="button"
                onClick={onClose}
                style={styles.cancelButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loading}
                style={{
                  ...styles.saveButton,
                  ...((saving || loading) ? styles.saveButtonDisabled : {}),
                }}
                onMouseEnter={(e) => {
                  if (!saving && !loading) {
                    e.currentTarget.style.backgroundColor = styles.saveButtonHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving && !loading) {
                    e.currentTarget.style.backgroundColor = styles.saveButton.backgroundColor;
                  }
                }}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add keyframe animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default UserRolesModal;